/**
 * URL State Management Service
 * Handles URL parameter management, navigation state persistence, and automatic project loading
 */

class URLStateService {
  constructor() {
    this.listeners = new Set();
    this.currentParams = new URLSearchParams(window.location.search);
    this.isInitialized = false;
  }

  /**
   * Initialize the URL state service
   */
  initialize() {
    if (this.isInitialized) {
      console.log('🔗 URL State Service already initialized');
      return;
    }

    console.log('🔗 Initializing URL State Service...');
    
    // Listen for popstate events (browser back/forward)
    window.addEventListener('popstate', this.handlePopState.bind(this));
    
    // Listen for pushstate/replacestate events
    this.interceptHistoryMethods();
    
    this.isInitialized = true;
    console.log('✅ URL State Service initialized');
  }

  /**
   * Intercept history methods to detect programmatic navigation
   */
  interceptHistoryMethods() {
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = (...args) => {
      originalPushState.apply(window.history, args);
      this.handleURLChange('pushstate');
    };

    window.history.replaceState = (...args) => {
      originalReplaceState.apply(window.history, args);
      this.handleURLChange('replacestate');
    };
  }

  /**
   * Handle popstate events
   */
  handlePopState(event) {
    console.log('🔗 Popstate event detected:', event);
    this.handleURLChange('popstate');
  }

  /**
   * Handle URL changes
   */
  handleURLChange(type) {
    const newParams = new URLSearchParams(window.location.search);
    const oldParams = this.currentParams;
    
    // Check if parameters actually changed
    const hasChanged = this.hasParametersChanged(oldParams, newParams);
    
    if (hasChanged) {
      console.log('🔗 URL parameters changed:', {
        type,
        old: Object.fromEntries(oldParams),
        new: Object.fromEntries(newParams)
      });
      
      this.currentParams = newParams;
      this.notifyListeners(type, {
        oldParams: Object.fromEntries(oldParams),
        newParams: Object.fromEntries(newParams),
        pathname: window.location.pathname
      });
    }
  }

  /**
   * Check if URL parameters have changed
   */
  hasParametersChanged(oldParams, newParams) {
    const oldEntries = Array.from(oldParams.entries()).sort();
    const newEntries = Array.from(newParams.entries()).sort();
    
    if (oldEntries.length !== newEntries.length) {
      return true;
    }
    
    for (let i = 0; i < oldEntries.length; i++) {
      if (oldEntries[i][0] !== newEntries[i][0] || oldEntries[i][1] !== newEntries[i][1]) {
        return true;
      }
    }
    
    return false;
  }

  /**
   * Get current URL parameters
   */
  getParams() {
    return Object.fromEntries(new URLSearchParams(window.location.search));
  }

  /**
   * Get specific parameter value
   */
  getParam(key) {
    return new URLSearchParams(window.location.search).get(key);
  }

  /**
   * Set URL parameter
   */
  setParam(key, value, options = {}) {
    const { replace = false, silent = false } = options;
    
    const url = new URL(window.location);
    if (value === null || value === undefined) {
      url.searchParams.delete(key);
    } else {
      url.searchParams.set(key, value);
    }
    
    if (replace) {
      window.history.replaceState({}, '', url);
    } else {
      window.history.pushState({}, '', url);
    }
    
    if (!silent) {
      this.handleURLChange(replace ? 'replacestate' : 'pushstate');
    }
  }

  /**
   * Set multiple URL parameters
   */
  setParams(params, options = {}) {
    const { replace = false, silent = false } = options;
    
    const url = new URL(window.location);
    
    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined) {
        url.searchParams.delete(key);
      } else {
        url.searchParams.set(key, value);
      }
    });
    
    if (replace) {
      window.history.replaceState({}, '', url);
    } else {
      window.history.pushState({}, '', url);
    }
    
    if (!silent) {
      this.handleURLChange(replace ? 'replacestate' : 'pushstate');
    }
  }

  /**
   * Remove URL parameter
   */
  removeParam(key, options = {}) {
    this.setParam(key, null, options);
  }

  /**
   * Clear all URL parameters
   */
  clearParams(options = {}) {
    const { replace = false, silent = false } = options;
    
    const url = new URL(window.location);
    url.search = '';
    
    if (replace) {
      window.history.replaceState({}, '', url);
    } else {
      window.history.pushState({}, '', url);
    }
    
    if (!silent) {
      this.handleURLChange(replace ? 'replacestate' : 'pushstate');
    }
  }

  /**
   * Navigate to URL with parameters
   */
  navigateTo(pathname, params = {}, options = {}) {
    const { replace = false } = options;
    
    const url = new URL(window.location.origin + pathname);
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        url.searchParams.set(key, value);
      }
    });
    
    if (replace) {
      window.history.replaceState({}, '', url);
    } else {
      window.history.pushState({}, '', url);
    }
    
    this.handleURLChange(replace ? 'replacestate' : 'pushstate');
  }

  /**
   * Get project ID from URL
   */
  getProjectId() {
    return this.getParam('id');
  }

  /**
   * Set project ID in URL
   */
  setProjectId(projectId, options = {}) {
    this.setParam('id', projectId, { replace: true, ...options });
  }

  /**
   * Get tab from URL
   */
  getTab() {
    return this.getParam('tab') || 'overview';
  }

  /**
   * Set tab in URL
   */
  setTab(tab, options = {}) {
    this.setParam('tab', tab, { replace: true, ...options });
  }

  /**
   * Ensure project ID is in URL, redirect to first available if not
   */
  async ensureProjectId(apiService, organizationId) {
    const currentProjectId = this.getProjectId();
    
    if (currentProjectId) {
      console.log('🔗 Project ID found in URL:', currentProjectId);
      return currentProjectId;
    }
    
    console.log('🔗 No project ID in URL, finding first available project...');
    
    try {
      const projects = await apiService.projects.getAll(organizationId);
      
      if (projects && projects.length > 0) {
        const firstProject = projects[0];
        console.log('🔗 Setting first available project in URL:', firstProject.id);
        this.setProjectId(firstProject.id, { silent: false });
        return firstProject.id;
      } else {
        console.warn('🔗 No projects available for organization:', organizationId);
        return null;
      }
    } catch (error) {
      console.error('🔗 Failed to load projects for URL management:', error);
      return null;
    }
  }

  /**
   * Add listener for URL changes
   */
  addListener(callback) {
    console.log('🔗 Adding URL state listener');
    this.listeners.add(callback);
    
    return () => {
      console.log('🔗 Removing URL state listener');
      this.listeners.delete(callback);
    };
  }

  /**
   * Notify all listeners
   */
  notifyListeners(type, data) {
    this.listeners.forEach(callback => {
      try {
        callback(type, data);
      } catch (error) {
        console.error('Error in URL state listener:', error);
      }
    });
  }

  /**
   * Cleanup service
   */
  cleanup() {
    console.log('🔗 Cleaning up URL State Service...');
    
    window.removeEventListener('popstate', this.handlePopState.bind(this));
    this.listeners.clear();
    this.isInitialized = false;
    
    console.log('✅ URL State Service cleaned up');
  }
}

// Create singleton instance
const urlStateService = new URLStateService();

export default urlStateService;
